/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;


// REGLE DE GESTION V3.9.0 : compatibilite des roles historiques.
// Tant qu'un role ne possede aucun droit menu:*, son acces est derive de ses droits metier.
const LEGACY_MENU_PERMISSION_FALLBACK = {
  'menu:dashboard': true, 'menu:about': true, 'menu:projects': ['projects:read'],
  'menu:tasks': ['tasks:read'], 'menu:workload': ['workload:read'], 'menu:absences': ['absences:read'],
  'menu:requirements': ['requirements:read'], 'menu:time': ['time:read'], 'menu:tests': ['tests:read'],
  'menu:sprints': ['sprints:read','milestones:read'], 'menu:governance': ['governance:read'],
  'menu:bi': ['bi:read'], 'menu:invoicing': ['invoicing:read'], 'menu:expenses': ['expenses:read'],
  'menu:validations': ['cra:read'], 'menu:interventions':['interventions:read'], 'menu:assets':['assets:read'], 'menu:admin': ['admin:users:read','admin:settings','admin:roles'],
  'menu:settings': ['admin:settings','clients:read','teams:read','notifications:read'], 'menu:technical':['technical:simulator'],
};
const LEGACY_SETTINGS_PERMISSION_FALLBACK = {
  'settings:clients':['clients:read'], 'settings:providers':['providers:read','admin:settings'],
  'settings:services':['teams:read','admin:settings'], 'settings:teams':['teams:read','admin:settings'],
  'settings:absences':['absences:read','admin:settings'], 'settings:users':['admin:users:read','admin:settings'],
  'settings:referentials':['admin:settings'], 'settings:notifications':['notifications:read','notifications:templates','admin:settings'],
  'settings:general':['admin:settings'],
};

// Configure axios defaults
axios.defaults.withCredentials = true;

// Error formatter
function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [enabledMenus, setEnabledMenus] = useState({});

  const fetchEnabledMenus = useCallback(async () => {
    try {
      const settingsRes = await axios.get(`${API_URL}/api/admin/settings`, { withCredentials: true });
      setEnabledMenus(settingsRes.data?.enabled_menus || {});
    } catch { /* non-blocking */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
      await fetchEnabledMenus();
    } catch (error) {
      // REGLE DE GESTION : le jeton d'acces dure moins longtemps que la session.
      // Sur 401 on tente donc un renouvellement avant de deconnecter l'utilisateur.
      if (error.response?.status === 401) {
        try {
          await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
          const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
          setUser(response.data);
          await fetchEnabledMenus();
          return;
        } catch (_) {
          setUser(false);
        }
      } else {
        // Une coupure reseau / erreur 5xx ne doit pas vider l'etat d'une page deja ouverte.
        setUser((previous) => previous || false);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchEnabledMenus]);

  useEffect(() => {
    // REGLE DE GESTION V3.8 : l'authentification Planigo est toujours verifiee
    // localement. Google Agenda n'intervient jamais dans ce flux de connexion.
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Recharge /api/auth/me immediately so role permissions are available
      // before MainLayout computes the visible menu.
      await checkAuth();
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };



  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: newPassword });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const roleCodes = user?.role_codes || [user?.role, ...(user?.additional_roles || [])].filter(Boolean);
  const effectivePermissions = user?.permissions || [];
  const value = {
    user,
    loading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    checkAuth,
    isAuthenticated: !!user,
    roleCodes,
    isAdmin: roleCodes.includes('admin') || effectivePermissions.includes('*'),
    isSupervisor: roleCodes.includes('supervisor'),
    isManager: roleCodes.some(role => ['admin', 'manager', 'project_manager', 'supervisor', 'pmo'].includes(role)) || effectivePermissions.includes('*'),
    isTeamLeader: !!user?.is_team_leader,
    permissions: effectivePermissions,
    enabledMenus,
    fetchEnabledMenus,
    isModuleEnabled: (path) => {
      // Admin always sees all modules. Others: only if not explicitly disabled.
      if (roleCodes.includes('admin') || effectivePermissions.includes('*')) return true;
      return enabledMenus[path] !== false;
    },
    hasPermission: (perm) => {
      const perms = user?.permissions || [];
      if (roleCodes.includes('admin') || perms.includes('*')) return true;
      if (perms.includes(perm)) return true;
      const base = perm.split(':')[0];
      if (perms.includes(`${base}:*`)) return true;
      if (perm.startsWith('menu:') && !perms.some(p => String(p).startsWith('menu:'))) {
        const legacy = LEGACY_MENU_PERMISSION_FALLBACK[perm];
        if (legacy === true) return true;
        if (Array.isArray(legacy)) return legacy.some(required => perms.includes(required) || perms.includes(`${required.split(':')[0]}:*`));
      }
      if (perm.startsWith('settings:') && !perms.some(p => String(p).startsWith('settings:'))) {
        const legacy = LEGACY_SETTINGS_PERMISSION_FALLBACK[perm];
        if (Array.isArray(legacy)) return legacy.some(required => perms.includes(required) || perms.includes(`${required.split(':')[0]}:*`));
      }
      return false;
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
